import { supabase } from '../config/supabaseClient';
import { ENV } from '../config/env';
import { withErrorHandling } from '../utils/errorHandler';
import { SocialPlatform } from '../types/socialMediaPlanner';

/**
 * Interface for content generation request options
 */
export interface ContentGenerationOptions {
  // Base content request options
  contentType: 'post' | 'caption' | 'hashtags' | 'engagement';
  topic?: string;
  targetAudience?: string;
  tone?: 'professional' | 'casual' | 'humorous' | 'inspirational' | 'educational';
  platforms: SocialPlatform[];
  
  // Optional - content specific params
  contentLength?: 'short' | 'medium' | 'long';
  includeHashtags?: boolean;
  includeEmojis?: boolean;
  includeQuestion?: boolean;
  
  // Existing post context (for variations/improvements)
  existingContent?: string;

  // User identification (optional in interface, required by API)
  userId?: string;
}

/**
 * Response types for content generation
 */
export interface GeneratedContent {
  content: string;
  suggestedHashtags?: string[];
  variations?: string[];
  platformSpecific?: Record<SocialPlatform, string>;
}

/**
 * Service for generating social media content using Claude
 */
export class SocialMediaContentService {
  /**
   * Get the authentication token from Supabase
   */
  private async getToken(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token || '';
    } catch (e) {
      console.error('Error getting Supabase auth token:', e);
      return '';
    }
  }

  /**
   * Make a request to the backend API
   */
  private async makeRequest<T>(endpoint: string, data: any): Promise<T> {
    const url = `${ENV.API_URL}${endpoint}`;

    try {
      console.log(`Making POST request to ${url}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getToken()}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API request failed:', response.status, errorText);
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  /**
   * Generate social media content with Claude
   */
  async generateContent(options: ContentGenerationOptions): Promise<GeneratedContent> {
    return withErrorHandling(async () => {
      // Validate required options
      if (!options.platforms || options.platforms.length === 0) {
        throw new Error('At least one platform is required');
      }

      if (!options.contentType) {
        throw new Error('Content type is required');
      }
      
      // Get user ID from session if not provided in options
      let userId = options.userId;
      if (!userId) {
        const { data } = await supabase.auth.getSession();
        userId = data.session?.user?.id;
        if (!userId) {
          throw new Error('User must be authenticated to generate content');
        }
      }
      
      // Set defaults for optional fields
      const completeOptions = {
        ...options,
        userId, // Include userId in the request
        contentLength: options.contentLength || 'medium',
        tone: options.tone || 'professional',
        includeHashtags: options.includeHashtags ?? true,
        includeEmojis: options.includeEmojis ?? true
      };

      // Make the API request
      const response = await this.makeRequest<GeneratedContent>(
        '/api/socialmediaplanner/social-content/generate',
        completeOptions
      );
      
      return response;
    }, { service: 'SocialMediaContentService', method: 'generateContent' });
  }

  /**
   * Generate post variations with Claude
   */
  async generateVariations(content: string, platforms: SocialPlatform[], count: number = 3): Promise<string[]> {
    return withErrorHandling(async () => {
      // Validate required parameters
      if (!content || content.trim() === '') {
        throw new Error('Content is required to generate variations');
      }

      if (!platforms || platforms.length === 0) {
        throw new Error('At least one platform is required');
      }

      // Make the API request
      const response = await this.makeRequest<{ variations: string[] }>(
        '/api/social-content/variations',
        {
          content,
          platforms,
          count
        }
      );
      
      return response.variations;
    }, { service: 'SocialMediaContentService', method: 'generateVariations' });
  }

  /**
   * Generate hashtag recommendations for content and platforms
   */
  async generateHashtags(content: string, platforms: SocialPlatform[], count: number = 10): Promise<string[]> {
    return withErrorHandling(async () => {
      // Validate required parameters
      if (!content || content.trim() === '') {
        throw new Error('Content is required to generate hashtags');
      }
      
      if (!platforms || platforms.length === 0) {
        throw new Error('At least one platform is required');
      }

      // Make the API request
      const response = await this.makeRequest<{ hashtags: string[] }>(
        '/api/social-content/hashtags',
        {
          content,
          platforms,
          count
        }
      );
      
      return response.hashtags;
    }, { service: 'SocialMediaContentService', method: 'generateHashtags' });
  }

  /**
   * Generate platform-specific versions of content
   */
  async optimizeForPlatforms(content: string, platforms: SocialPlatform[]): Promise<Record<SocialPlatform, string>> {
    return withErrorHandling(async () => {
      // Validate required parameters
      if (!content || content.trim() === '') {
        throw new Error('Content is required for platform optimization');
      }
      
      if (!platforms || platforms.length === 0) {
        throw new Error('At least one platform is required');
      }

      // Make the API request
      const response = await this.makeRequest<{ optimizedContent: Record<SocialPlatform, string> }>(
        '/api/social-content/optimize',
        {
          content,
          platforms
        }
      );
      
      return response.optimizedContent;
    }, { service: 'SocialMediaContentService', method: 'optimizeForPlatforms' });
  }
}

// Create a singleton instance
export const socialMediaContentService = new SocialMediaContentService();
